import { MutableRefObject } from "react";

import { Portal } from "../../../Common/Components/Portal";
import { DropdownButtonDirection, DropdownButtonSize } from "./DropdownButton";
import { DropdownItem } from "./DropdownItem";

import styles from "./DropdownButton.module.scss";

interface IDropdownContainerProps {
    /**
     * The reference for the clickOutside of the dropdown.
     */
    dropdownRef: MutableRefObject<HTMLDivElement>;

    /**
     * The reference of the div the dropdown needs to align with.
     */
    targetRef: MutableRefObject<HTMLDivElement>;

    /**
     * The size of the dropdown.
     */
    size?: DropdownButtonSize;

    /**
     * The direction where the dropdown will open.
     */
    direction?: DropdownButtonDirection;

    /**
     * Array of items with props shown when dropdown is open
     */
    items: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        iconButton?: JSX.Element;
    }[];

    /**
     * An action to perform after clicking on an item.
     */
    onItemClickCallback?: (item:
    {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    }) => void;
}

/**
 * The dropdown container we see when the dropdown is open.
 * @param {IDropdownContainerProps} param The props used to render the dropdown container.
 * @returns The rendered component.
 */
function DropdownContainer({
    dropdownRef,
    targetRef,
    size,
    direction,
    items,
    onItemClickCallback
}: Readonly<IDropdownContainerProps>): JSX.Element {
    return (
        <Portal
            targetRef={targetRef}
            direction={direction}
            minWidth={targetRef.current?.offsetWidth}
        >
            <div ref={dropdownRef} className={styles.dropdownContainer}>
                {items.map((item, index) => (
                    <DropdownItem
                        id={`dropdownItem-${index + 1}`}
                        key={`dropdownItem-${index + 1}`}
                        size={size}
                        label={item.label}
                        disabled={item.disabled ?? false}
                        onClick={() => onItemClickCallback?.(item)}
                        iconButton={item.iconButton}
                    />
                ))}
            </div>
        </Portal>
    );
}

export { DropdownContainer, IDropdownContainerProps };
